import dayjs from 'dayjs';
import { tKey } from 'ember-intl';
import { RI_BREAKDOWNS } from 'frontend-utils/utils/constants/constant-values';
import { getHotelNameMapping } from 'frontend-utils/utils/hash';
import { PARTIAL_DATE_FORMATS } from 'lumen/utils/constants/general-constants';
import {
  PMS_MAPPING_SEGMENT_TRANSLATIONS,
  PMS_MAPPING_TRAVEL_AGENT_TRANSLATIONS,
  PMS_MAPPING_WEEKDAY_TRANSLATIONS,
} from 'lumen/utils/constants/pms-mapping-constants';
import { KPI_CATEGORIES } from 'lumen/utils/constants/ri-constant-values';

const UNACCOUNTED_GROUP = 'revenue_insight.unaccounted_group';

const subscriptionMap = {};

export function segmentationIdFormatter(segmentationId, selections) {
  if (
    segmentationId === RI_BREAKDOWNS.PICKUP &&
    selections.isCancellationsOnlyView
  ) {
    return tKey('revenue_insight.cancellation_pickup');
  }

  return `revenue_insight.${segmentationId}`;
}

export function segmentationValueFormatter(
  segmentationId,
  segmentationValue,
  extaFormatterParams,
) {
  const { intl, subscriptionName, isFuture, demoMode } = extaFormatterParams;
  const strippedSegmentationValue = segmentationValue.replace('parent-', '');

  if (
    segmentationId === RI_BREAKDOWNS.SEGMENT &&
    strippedSegmentationValue in PMS_MAPPING_SEGMENT_TRANSLATIONS
  ) {
    const key = PMS_MAPPING_SEGMENT_TRANSLATIONS[strippedSegmentationValue];
    return intl.t(key).toString();
  }

  if (
    segmentationId === RI_BREAKDOWNS.TRAVEL_AGENT &&
    strippedSegmentationValue in PMS_MAPPING_TRAVEL_AGENT_TRANSLATIONS
  ) {
    const key =
      PMS_MAPPING_TRAVEL_AGENT_TRANSLATIONS[strippedSegmentationValue];
    return intl.t(key).toString();
  }

  if (
    segmentationId === RI_BREAKDOWNS.WEEKDAY &&
    strippedSegmentationValue in PMS_MAPPING_WEEKDAY_TRANSLATIONS
  ) {
    const key = PMS_MAPPING_WEEKDAY_TRANSLATIONS[strippedSegmentationValue];
    return intl.t(key).toString();
  }

  // Use the subscription name as displayValue instead of the subscription id
  if (segmentationId === RI_BREAKDOWNS.SUBSCRIPTION_ID) {
    if (subscriptionName) {
      const mappedHotelNameInfo = getHotelNameMapping(
        demoMode,
        subscriptionName,
      );
      const name = mappedHotelNameInfo
        ? `${intl.t(tKey('general.hotel.plural'), { count: 1 })} ${mappedHotelNameInfo.id}`
        : subscriptionName;
      subscriptionMap[segmentationValue] = name;
      return name;
    }

    // For the displayValue of the filters in the filterbar we only have segmentationId + subscription ids (no extaFormatterParams with subscription name)
    // We rely on the subscriptionMap (constructed at page load) to provide the subscription name based on the id
    if (subscriptionMap[segmentationValue]) {
      return subscriptionMap[segmentationValue];
    }
  }

  if (segmentationId === RI_BREAKDOWNS.WEEKDAY) {
    const weekdayTranslations = {
      1: 'days.mon',
      2: 'days.tue',
      3: 'days.wed',
      4: 'days.thu',
      5: 'days.fri',
      6: 'days.sat',
      7: 'days.sun',
      weekday: tKey('week.weekday'),
      weekend: tKey('week.weekend'),
    };

    if (weekdayTranslations[strippedSegmentationValue]) {
      return intl.t(weekdayTranslations[strippedSegmentationValue]).toString();
    }
  } else if (
    segmentationId === RI_BREAKDOWNS.PICKUP &&
    strippedSegmentationValue.includes(
      `revenue_insight.${RI_BREAKDOWNS.PICKUP}`,
    )
  ) {
    /*
      Example of revenue_insight.pickup segmentation values passed by API:
      - revenue_insight.pickup.x => use general days ago translation key
      - revenue_insight.pickup.x.y => use different 'From x till y' translation

      x value will still be used for sorting

      Notes: for dates in the future we show 'X days ago'. For dates in the past we use 'X days before'
    */

    const splittedSegmentationValue = strippedSegmentationValue.split('.');
    const xValue = splittedSegmentationValue[2];

    // Use separate translation if there is an y value in the segmentation value
    // translation will be "last x to y days of the period" for past dates
    // or "between x and y days ago" for future dates
    if (splittedSegmentationValue[3]) {
      if (isFuture) {
        return intl
          .t(tKey('revenue_insight.pickup_from_until.future_dates'), {
            x: xValue,
            y: splittedSegmentationValue[3],
          })
          .toString();
      }
      return intl
        .t(tKey('revenue_insight.pickup_from_until.past_dates'), {
          x: xValue,
          y: splittedSegmentationValue[3],
        })
        .toString();
    }
    // If there's no y value we can return a simpler translation
    if (isFuture) {
      if (xValue === '0') {
        // Special case for protel
        // it has 0 as well which translates to 0
        // example: https://test.mylighthouse.com/hotel/106740/revenue
        return intl.t(tKey('general.today')).toString();
      }
      // for the normal case we have 2 cases
      // "yesterday" if count is one
      // "last x days" for all others
      return intl
        .t(tKey('revenue_insight.daysago'), { count: xValue })
        .toString();
    }
    // for the past case we also have 2 cases
    // "Last day of the period" if count is one
    // "last x days of the period" for all others
    return intl
      .t(tKey('revenue_insight.daysago.past_dates'), { count: xValue })
      .toString();
  } else if (
    segmentationId === RI_BREAKDOWNS.IMPACT_DATE ||
    segmentationId === RI_BREAKDOWNS.PACE
  ) {
    const dividerIndex = strippedSegmentationValue.indexOf(':');

    if (dividerIndex !== -1) {
      const format = 'll';
      const startDate = strippedSegmentationValue.substring(0, dividerIndex);
      const endDate = strippedSegmentationValue.substring(dividerIndex + 1);

      return `${dayjs.utc(startDate).format(format)} - ${dayjs.utc(endDate).format(format)}`;
    }
    return dayjs
      .utc(strippedSegmentationValue)
      .format(PARTIAL_DATE_FORMATS[dayjs().locale()].WEEKDAY_AND_DATE_LONG);
  } else if (strippedSegmentationValue === UNACCOUNTED_GROUP && isFuture) {
    return intl.t(`${UNACCOUNTED_GROUP}_future`).toString();
  } else if (
    strippedSegmentationValue === `revenue_insight.${KPI_CATEGORIES.BLANK}`
  ) {
    const key = `revenue_insight.${KPI_CATEGORIES.BLANK}.${segmentationId}`;

    if (!intl.exists(key)) {
      return intl.t(strippedSegmentationValue).toString();
    }

    return intl.t(key).toString();
  } else if (strippedSegmentationValue.includes('revenue_insight.')) {
    return intl.t(strippedSegmentationValue).toString();
  }

  return strippedSegmentationValue;
}
